/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FunctionComponent } from "react";
import Select, { Options } from "react-select";
import { useTranslation } from "react-i18next";
import useAnonymizeDropdown, {
  AnonymizeValues,
} from "../../../hooks/useAnonymizeDropdown";
import {
  Anonymize,
  defaultAnonymize,
  TransformData,
  TransformFieldProps,
} from "../../../types/rule";
import { ANONYMIZE, getSelectStyles } from "../../../constant";
import { OptionTypeBase } from "../../../types/common";

const AnonymizeDicom: FunctionComponent<TransformFieldProps> = ({
  disabled,
  id,
  transform,
  onValueChangeHandler,
  isDark = false,
  fullWidth = false,
}: TransformFieldProps) => {
  const { t } = useTranslation("form");
  const anonymizeOptions = useAnonymizeDropdown();

  const dispatchNewValue = (trans: TransformData) => {
    onValueChangeHandler({
      type: ANONYMIZE,
      id,
      transform: trans,
    });
  };

  const updateAnonymize = (items: Options<any>) => {
    let data = defaultAnonymize;
    if (items.length) {
      items.forEach((otp: any) => {
        switch (otp.value) {
          case AnonymizeValues.RETAIN_DEVICE:
            data = {
              ...data,
              retainDeviceIdentityOption: true,
            };
            break;
          case AnonymizeValues.RETAIN_INSTITUTION:
            data = {
              ...data,
              retainInstitutionIdentityOption: true,
            };
            break;
          case AnonymizeValues.RETAIN_RETAIN_UIDS:
            data = {
              ...data,
              retainUidsOption: true,
            };
            break;

          case AnonymizeValues.RETAIN_LONGITUDINAL:
            data = {
              ...data,
              retainLongitudinalTemporalInformationFullDatesOption: true,
            };
            break;
          case AnonymizeValues.RETAIN_DESCRIPTIONS_OPTION:
            data = {
              ...data,
              retainDescriptionsOption: true,
            };
            break;
          case AnonymizeValues.RETAIN_BURNED_IN_ANNOTATIONS_OPTION:
            data = {
              ...data,
              retainBurnedInAnnotationsOption: true,
            };
            break;
          default:
            break;
        }
      });
    }

    dispatchNewValue({ ...transform, anonymize: data });
  };

  const findSelectedVal = (anonymize?: Anonymize) => {
    const values: OptionTypeBase[] = [];
    if (anonymize) {
      if (anonymize.retainDeviceIdentityOption) {
        values.push(
          anonymizeOptions.find(
            (x) => x.value === AnonymizeValues.RETAIN_DEVICE
          ) as OptionTypeBase
        );
      }

      if (anonymize.retainInstitutionIdentityOption) {
        values.push(
          anonymizeOptions.find(
            (x) => x.value === AnonymizeValues.RETAIN_INSTITUTION
          ) as OptionTypeBase
        );
      }

      if (anonymize.retainUidsOption) {
        values.push(
          anonymizeOptions.find(
            (x) => x.value === AnonymizeValues.RETAIN_RETAIN_UIDS
          ) as OptionTypeBase
        );
      }

      if (anonymize.retainLongitudinalTemporalInformationFullDatesOption) {
        values.push(
          anonymizeOptions.find(
            (x) => x.value === AnonymizeValues.RETAIN_LONGITUDINAL
          ) as OptionTypeBase
        );
      }

      if (anonymize.retainDescriptionsOption) {
        values.push(
          anonymizeOptions.find(
            (x) => x.value === AnonymizeValues.RETAIN_DESCRIPTIONS_OPTION
          ) as OptionTypeBase
        );
      }

      // Not an option while its feature flag is off
      const retainBurnedInAnnotations = anonymizeOptions.find(
        (x) => x.value === AnonymizeValues.RETAIN_BURNED_IN_ANNOTATIONS_OPTION
      );
      if (
        anonymize.retainBurnedInAnnotationsOption &&
        retainBurnedInAnnotations
      ) {
        values.push(retainBurnedInAnnotations);
      }
    }
    return values;
  };

  const selectComponent = (
    <Select
      menuPlacement="auto"
      menuPosition="fixed"
      id={`${id}_anonymize`}
      isDisabled={disabled}
      options={anonymizeOptions}
      styles={getSelectStyles(isDark)}
      value={findSelectedVal(transform?.anonymize)}
      placeholder={t("no_options_selected")}
      onChange={(items) => updateAnonymize(items as Options<any>)}
      isMulti
    />
  );

  return (
    <>
      {!fullWidth ? (
        <>
          <div className="row">
            <div className="col-md-8 col-xs-12 mb-1">{selectComponent}</div>
          </div>
        </>
      ) : (
        selectComponent
      )}
    </>
  );
};

export default AnonymizeDicom;
